import classNames from 'classnames';
import React, { FC, useEffect, useReducer } from 'react';

import { LinkButton } from '@eika/button';
import { FileUploader } from '@eika/file-uploader';
import {
    Attachment,
    AttachmentActionType,
    AttachmentStatus,
    defaultOptions,
    FileUploaderOptions,
    ONE_MEGABYTE,
    updateAttachments,
} from '@eika/file-uploader/lib/file-uploader-types';
import { Icon, IconTypes } from '@eika/icon';
import { InputLabel } from '@eika/input';

import { VedleggWeb } from '../../generated-types';
import { useDeleteLeiekontrakt } from '../../hooks/useInntekt';
import { useTranslation } from '../../translations';

export const fileUploaderDefaultOptions: FileUploaderOptions = {
    dropAreaText: defaultOptions.dropAreaText,
    dropButtonLabel: defaultOptions.dropButtonLabel,
    attachmentListHeading: 'Dokumenter',
    maxFileByteSize: 10 * ONE_MEGABYTE,
    maxCombinedUploadByteSize: 25 * ONE_MEGABYTE,
    largeFormatFileMinByteSize: 1024,
    allowedFileTypes: ['jpg', 'jpeg', 'png', 'pdf', 'doc', 'docx', 'xls', 'xlsx'],
    largeFormatFileTypes: ['jpg', 'jpeg', 'png', 'pdf', 'doc', 'docx', 'xls', 'xlsx'],
};

interface Props {
    soeknadId: string | undefined;
    leiekontrakter: VedleggWeb[] | undefined;
    lastOppUtleiekontrakt: (attachment: Attachment[]) => void;
}

const Uteleiekontrakt: FC<Props> = ({ soeknadId, leiekontrakter, lastOppUtleiekontrakt }) => {
    const { leieinntekt } = useTranslation();
    const [attachments, dispatch] = useReducer(updateAttachments, []);
    const deleteLeiekontrakt = useDeleteLeiekontrakt();

    useEffect(() => {
        lastOppUtleiekontrakt(attachments);
    }, [attachments, lastOppUtleiekontrakt]);

    const leggTilUteleiekontrakt = async (attachments: Attachment[]) => {
        return new Promise<Attachment[]>((resolve) => {
            resolve(
                attachments.map((attachment) => ({
                    ...attachment,
                    status: AttachmentStatus.Ok,
                })),
            );
            dispatch({ type: AttachmentActionType.ADD_ATTACHMENTS, payload: attachments });
        });
    };

    const fjernUteleiekontrakt = (attachment: Attachment) => {
        dispatch({ type: AttachmentActionType.REMOVE_ATTACHMENT, payload: [attachment] });
    };

    const onDelete = (dokumentNavn: string) => {
        if (soeknadId) {
            deleteLeiekontrakt.mutate({ soeknadId, dokumentNavn });
        }
    };

    return (
        <div>
            <InputLabel label={leieinntekt.subTitleUtleiekontrakt} />
            <p>{leieinntekt.infoUteleikontrakt1}</p>
            <p>{leieinntekt.infoUteleikontrakt2}</p>
            <FileUploader
                uploadAttachment={leggTilUteleiekontrakt}
                removeAttachment={fjernUteleiekontrakt}
                options={fileUploaderDefaultOptions}
            />
            <div className="sgw-file-uploader">
                <ul className="file-uploader-list">
                    {leiekontrakter?.map((kontrakt) => (
                        <li key={kontrakt.dokumentNavn} className={classNames('file-uploader-list-element')}>
                            <Icon type={IconTypes.DOKUMENT_24} />
                            <div className="typography-subtitle file-uploader-list-element-file-name">
                                {kontrakt.dokumentNavn}
                            </div>
                            <LinkButton onClick={() => onDelete(kontrakt.dokumentNavn)}>Slett</LinkButton>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
};

export default Uteleiekontrakt;
